import { observer } from "mobx-react-lite";
import { DatabaseIcon, HashIcon } from "lucide-react";

import { CountConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { custom, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const types = [
  { value: "chars", label: "Characters" },
  { value: "words", label: "Words" },
  { value: "sentences", label: "Sentences" },
  { value: "paragraphs", label: "Paragraphs" },
] as const;

const parseOptionalCount = (value: string): number | null | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const formatOptionalCount = (value: number | null | undefined): string =>
  typeof value === "number" ? String(value) : "";

const validationSchema = createBlockSchema<CountConfiguration>()
  .requireName()
  .addRule(required("configuration.type", "Count type is required"))
  .addRule(required("configuration.source", "Source is required"))
  .addRule(
    custom(
      "configuration",
      (_, payload) => {
        const { min_count: minCount, max_count: maxCount } = payload.configuration ?? {};
        if (minCount === null || maxCount === null) {
          return false;
        }
        if (typeof minCount === "number" && typeof maxCount === "number" && minCount > maxCount) {
          return false;
        }
        return true;
      },
      "Min/max must be non-negative integers, and min cannot exceed max",
      ["configuration.min_count", "configuration.max_count"],
    ),
  )
  .build();

const CountBlockCore = observer(
  ({ sources = createEmptySources() }: Omit<BlockProps<CountConfiguration>, "DefaultFields">) => {
    const block = useBlock<CountConfiguration>();
    const isReadonly = useIsReadonly();

    const updateConfiguration = (patch: Partial<CountConfiguration>) => {
      block.setConfiguration({
        type: block.configuration?.type || "words",
        source: block.configuration?.source || "",
        ...block.configuration,
        ...patch,
      });
    };

    const handleTypeChange = (type: CountConfiguration["type"]) => {
      updateConfiguration({ type });
    };

    const handleBoundChange = (key: "min_count" | "max_count", value: string) => {
      const parsed = parseOptionalCount(value);
      updateConfiguration({ [key]: parsed === undefined ? undefined : parsed });
    };

    return (
      <>
        <SectionGroup
          title="Count Configuration"
          description="Configure what to count in the data"
          icon={<HashIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="Count of" path="configuration.type">
              <Select
                value={block.configuration?.type || undefined}
                onValueChange={handleTypeChange}
                disabled={isReadonly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a type..." />
                </SelectTrigger>
                <SelectContent>
                  {types.map(typeOption => (
                    <SelectItem key={typeOption.value} value={typeOption.value}>
                      {typeOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ValidatedField>

            <div className="grid grid-cols-2 gap-3">
              <ValidatedField name="Min count" path="configuration.min_count">
                <Input
                  value={formatOptionalCount(block.configuration?.min_count)}
                  onChange={event => handleBoundChange("min_count", event.target.value)}
                  inputMode="numeric"
                  placeholder="Optional"
                  disabled={isReadonly}
                />
              </ValidatedField>
              <ValidatedField name="Max count" path="configuration.max_count">
                <Input
                  value={formatOptionalCount(block.configuration?.max_count)}
                  onChange={event => handleBoundChange("max_count", event.target.value)}
                  inputMode="numeric"
                  placeholder="Optional"
                  disabled={isReadonly}
                />
              </ValidatedField>
            </div>
            <p className="text-xs text-muted-foreground">
              Leave both blank to return the count. Set min and/or max to return a pass/fail check.
            </p>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the source to count elements from"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<CountConfiguration>(CountBlockCore, {
  validationSchema,
});
